'use client'

import type { ComponentProps } from 'react'
import { SupportedLanguages } from '@skop-app/lib-domain/types'
import { Select } from '../Select/select.atom'
import { reactSelectTheme } from '../Select/select.config'

type PeriodicSelectStyles = NonNullable<ComponentProps<typeof Select>['styles']>

export type PeriodValue =
  | 'today'
  | 'lastWeek'
  | 'currentWeek'
  | 'lastMonth'
  | 'month'
  | 'year'
  | 'lastYear'
  | 'fiveYears'
  | 'custom'

export interface PeriodOption {
  value: PeriodValue
  fr: string
  en: string
  nl: string
}

export interface PeriodicCalendarProps {
  value?: PeriodOption
  onChange?: (option: PeriodOption | null) => void
  placeholder?: string
  isDisabled?: boolean
  className?: string
  isClearable?: boolean
  language?: SupportedLanguages
  reactSelectTheme?: typeof reactSelectTheme
  reactSelectStyles?: PeriodicSelectStyles
}

const periodOptions: PeriodOption[] = [
  {
    value: 'today',
    fr: "Aujourd'hui",
    en: 'Today',
    nl: 'Vandaag',
  },
  {
    value: 'currentWeek',
    fr: 'Semaine en cours',
    en: 'Current week',
    nl: 'Huidige week',
  },
  {
    value: 'lastWeek',
    fr: 'Semaine dernière',
    en: 'Last week',
    nl: 'Vorige week',
  },
  {
    value: 'lastMonth',
    fr: 'Mois dernier',
    en: 'Last month',
    nl: 'Vorige maand',
  },
  {
    value: 'month',
    fr: 'Mois en cours',
    en: 'Current month',
    nl: 'Huidige maand',
  },
  {
    value: 'year',
    fr: 'Année en cours',
    en: 'Current year',
    nl: 'Huidige jaar',
  },
  {
    value: 'lastYear',
    fr: 'Année dernière',
    en: 'Last year',
    nl: 'Vorig jaar',
  },
  {
    value: 'fiveYears',
    fr: 'Les 5 dernières années',
    en: 'Last 5 years',
    nl: 'Laatste 5 jaar',
  },
]

export const PeriodicCalendar = ({
  value,
  onChange,
  placeholder,
  isDisabled = false,
  className,
  isClearable = true,
  language = 'fr',
  reactSelectTheme,
  reactSelectStyles,
}: Readonly<PeriodicCalendarProps>) => {
  const transformedOptions = periodOptions.map((option) => ({
    ...option,
    label: option[language],
    value: option.value,
  }))

  const transformedValue = value
    ? { ...value, label: value[language], value: value.value }
    : undefined

  const getDefaultPlaceholder = () => {
    if (language === 'en') return 'Select a period'
    if (language === 'nl') return 'Selecteer een periode'
    return 'Sélectionner une période'
  }

  return (
    <Select
      value={transformedValue}
      onChange={onChange}
      options={transformedOptions}
      placeholder={placeholder || getDefaultPlaceholder()}
      isDisabled={isDisabled}
      className={className}
      isClearable={isClearable}
      overflow
      theme={reactSelectTheme}
      styles={reactSelectStyles}
    />
  )
}
